import React, { useState, useEffect } from 'react';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import {
  Box, Typography, Button, Grid,
  Stepper, Step, StepLabel, Alert, CircularProgress
} from '@mui/material';
import { generateFacturX } from '../services/api';
import { useInvoiceForm } from '../hooks/useInvoiceForm';
import { useFormCalculations } from '../hooks/useFormCalculations';
import { useInvoiceData } from '../hooks/useInvoiceData';
import { useError } from '../hooks/useError';
import InvoiceInfoSection from '../components/form/InvoiceInfoSection';
import PartyInfoSection from '../components/form/PartyInfoSection';
import ItemsTable from '../components/form/ItemsTable';
import TotalsSection from '../components/form/TotalsSection';

const FormPage = () => {
  const { invoiceId } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  
  const [activeStep] = useState(1); // Verification step
  const { loading, error: dataError, getInitialData } = useInvoiceData(invoiceId, location.state);
  const { error, setError, handleError, clearError } = useError();
  const {
    formData,
    submitting,
    setSubmitting,
    handleInputChange,
    handleItemChange,
    addItem,
    removeItem,
    updateFormData,
    setFormData
  } = useInvoiceForm();
  
  useFormCalculations(formData, updateFormData);
  
  const steps = ['Upload', 'Vérification des données', 'Génération'];

  useEffect(() => {
    const initializeData = async () => {
      const data = await getInitialData();
      if (data) {
        setFormData(data);
      }
    };
    
    initializeData();
  }, [getInitialData, setFormData]);


  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    clearError();

    try {
      await generateFacturX(invoiceId, formData);
      navigate(`/success/${invoiceId}`);
    } catch (err) {
      handleError(err, 'Une erreur est survenue lors de la génération du Factur-X.');
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '50vh' }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box>
      <Stepper activeStep={activeStep} alternativeLabel sx={{ mb: 4 }}>
        {steps.map((label) => (
          <Step key={label}>
            <StepLabel>{label}</StepLabel>
          </Step>
        ))}
      </Stepper>

      <Typography variant="h4" component="h1" gutterBottom align="center">
        Vérification des données de la facture
      </Typography>
      
      <Typography variant="body1" paragraph align="center" sx={{ mb: 4 }}>
        Vérifiez et complétez les informations extraites de votre facture
      </Typography>

      {(error || dataError) && (
        <Alert severity="error" sx={{ mb: 3 }}>
          {error || dataError}
        </Alert>
      )}

      <form onSubmit={handleSubmit}>
        <InvoiceInfoSection 
          formData={formData} 
          handleInputChange={handleInputChange} 
        />

        <Grid container spacing={3} sx={{ mb: 4 }}>
          <Grid item xs={12} md={6}>
            <PartyInfoSection
              title="Informations du vendeur"
              formData={formData}
              handleInputChange={handleInputChange}
              prefix="seller"
            />
          </Grid>
          
          <Grid item xs={12} md={6}>
            <PartyInfoSection
              title="Informations du client"
              formData={formData}
              handleInputChange={handleInputChange}
              prefix="buyer"
            />
          </Grid>
        </Grid>

        <ItemsTable
          formData={formData}
          handleItemChange={handleItemChange}
          addItem={addItem}
          removeItem={removeItem}
        />

        <TotalsSection 
          formData={formData} 
          handleInputChange={handleInputChange} 
        />

        <Box sx={{ display: 'flex', justifyContent: 'center', gap: 2, mb: 4 }}>
          <Button
            variant="outlined"
            color="primary"
            size="large"
            onClick={() => navigate('/upload')}
            sx={{ px: 4 }}
          >
            Retour
          </Button>
          
          <Button
            type="submit"
            variant="contained"
            color="primary"
            size="large"
            disabled={submitting}
            sx={{ px: 4 }}
          >
            {submitting ? (
              <CircularProgress size={24} color="inherit" />
            ) : (
              'Générer le Factur-X'
            )}
          </Button>
        </Box>
      </form>
    </Box>
  );
};

export default FormPage;
